import React, { useEffect, useState } from 'react';
import { MainLayout } from '../components/layouts/MainLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { DataTable } from '../components/ui/data-table';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { usersAPI, rolesAPI } from '../lib/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../lib/firebase';
import { useToast } from '../components/ui/use-toast';
import { useAuth } from '../contexts/AuthContext';
import type { User, UserRole, Role } from '../types';
import { Shield, Users as UsersIcon, Edit, AlertCircle } from 'lucide-react';

interface UserWithRole extends User {
  role?: UserRole;
}

export const RolesPage: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>('app_admin');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, rolesData] = await Promise.all([
        usersAPI.getAll(),
        // Get all roles from Firestore
        rolesAPI.getAll(),
      ]);

      // Create a map of email -> role
      const rolesMap = new Map<string, UserRole>();
      rolesData.forEach((role: Role) => {
        rolesMap.set(role.email, role.role);
      });

      // Combine users with their roles
      const usersWithRoles: UserWithRole[] = usersData.map((user) => ({
        ...user,
        role: rolesMap.get(user.email),
      }));

      setUsers(usersWithRoles);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load users and roles',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (user: UserWithRole) => {
    setSelectedUser(user);
    setSelectedRole(user.role || 'app_admin');
    setIsEditDialogOpen(true);
  };

  const handleSetRole = async () => {
    if (!selectedUser) return;

    const isOwnRole = selectedUser.email === user?.email;

    try {
      setSaving(true);
      const setRoleFunction = httpsCallable(functions, 'setUserRole');
      await setRoleFunction({ email: selectedUser.email, role: selectedRole });

      toast({
        title: 'Success',
        description: `Role ${selectedRole} assigned to ${selectedUser.email}`,
        className: 'bg-green-50 border-green-200',
      });

      setIsEditDialogOpen(false);
      setSelectedUser(null);

      // If user changed their own role, force token refresh
      if (isOwnRole) {
        toast({
          title: 'Role updated',
          description: 'You will be logged out to refresh your permissions',
          className: 'bg-indigo-50 border-indigo-200',
        });

        // Wait 2 seconds then force logout to refresh token
        setTimeout(async () => {
          await auth.currentUser?.getIdToken(true); // Force token refresh
          window.location.href = '/login'; // Force re-login
        }, 2000);
      } else {
        await loadData();

        toast({
          title: 'Note',
          description: `${selectedUser.email} must log out and log back in to see the changes`,
          className: 'bg-yellow-50 border-yellow-200',
        });
      }
    } catch (error: any) {
      console.error('Error setting role:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to set role',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadge = (role?: UserRole) => {
    if (!role) {
      return <Badge variant="outline" className="text-gray-500">No role</Badge>;
    }

    const badgeStyles = {
      superadmin: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 border-purple-300 dark:border-purple-700',
      app_admin: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 border-indigo-300 dark:border-indigo-700',
      viewer: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-700',
    };

    const labels = {
      superadmin: 'Super Admin',
      app_admin: 'App Admin',
      viewer: 'Viewer',
    };

    return (
      <Badge variant="outline" className={badgeStyles[role]}>
        {labels[role]}
      </Badge>
    );
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Shield className="h-8 w-8" />
              User Roles
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage system-level user roles and permissions
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Loading users...</div>
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UsersIcon className="h-5 w-5" />
                All Users ({users.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                data={users}
                columns={[
                  {
                    key: 'fullName',
                    label: 'Name',
                    sortable: true,
                    render: (user) => <span className="font-medium">{user.fullName}</span>,
                  },
                  {
                    key: 'email',
                    label: 'Email',
                    sortable: true,
                  },
                  {
                    key: 'department',
                    label: 'Department',
                    sortable: true,
                  },
                  {
                    key: 'role',
                    label: 'Role',
                    sortable: true,
                    render: (user) => getRoleBadge(user.role),
                  },
                  {
                    key: 'actions',
                    label: 'Actions',
                    render: (user) => (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(user)}
                        className="gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Edit Role
                      </Button>
                    ),
                  },
                ]}
                searchKeys={['fullName', 'email', 'department']}
                emptyMessage="No users found"
                pageSize={25}
              />
            </CardContent>
          </Card>
        )}

        <Card className="bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <h3 className="font-semibold text-indigo-900 dark:text-indigo-100">Role Descriptions</h3>
              <ul className="text-sm text-indigo-800 dark:text-indigo-200 space-y-1">
                <li><strong>Super Admin:</strong> Full access to all features and settings</li>
                <li><strong>App Admin:</strong> Can manage assigned applications and participate in reviews</li>
                <li><strong>Viewer:</strong> Read-only access to reviews and applications</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit Role Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User Role</DialogTitle>
            <DialogDescription>
              Change the system role for {selectedUser?.fullName} ({selectedUser?.email})
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-2">Select Role</label>
              <Select value={selectedRole} onValueChange={(value) => setSelectedRole(value as UserRole)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="superadmin">Super Admin</SelectItem>
                  <SelectItem value="app_admin">App Admin</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectedUser?.email === user?.email ? (
              <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-red-800 dark:text-red-200">
                  <strong>Warning: Changing your own role!</strong>
                  <p className="mt-1">You will be logged out immediately after this change to refresh your permissions. Make sure you understand the new role's limitations.</p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-indigo-800 dark:text-indigo-200">
                  <strong>Automatic logout:</strong>
                  <p className="mt-1">The user's session will be automatically terminated and they will need to log in again to access the application with their new permissions.</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSetRole} disabled={saving}>
              {saving ? 'Saving...' : 'Save Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
